import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../../utils/firebase/firebase.utils';

// styles
import {
    PromoStatsContainer,
    HeaderContainer,
    StatsTable,
    CodeBadge,
    CountBadge,
    RedemptionList,
    RedemptionRow,
    RedemptionHeader,
    EmptyState,
    LoadingState,
} from './PromoStatsWidget.styles.js';

import Title from '../../../../components/widgets/components/title/Title.js';
import PrimaryBtn from '../../../../components/btns/primary-btn/PrimaryBtn.js';

function formatDate(timestamp) {
    if (!timestamp) return '—';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}



export default function PromoStatsWidget() {
    const [promoCodes, setPromoCodes] = useState([]);
    const [expandedCode, setExpandedCode] = useState(null);
    const [redemptions, setRedemptions] = useState({});
    const [loadingRedemptions, setLoadingRedemptions] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchPromoCodes = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const snapshot = await getDocs(collection(db, 'promoCodes'));
            const codes = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
            codes.sort((a, b) => (b.totalRedemptions || 0) - (a.totalRedemptions || 0));
            setPromoCodes(codes);
        } catch (err) {
            setError('Failed to load promo code stats.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPromoCodes();
    }, [fetchPromoCodes]);

    const handleToggleExpand = async (code) => {
        if (expandedCode === code) {
            setExpandedCode(null);
            return;
        }
        setExpandedCode(code);

        if (redemptions[code]) return;

        setLoadingRedemptions((prev) => ({ ...prev, [code]: true }));
        try {
            const snapshot = await getDocs(
                collection(db, 'promoCodes', code, 'redemptions')
            );
            const rows = snapshot.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .sort((a, b) => {
                    const aTime = a.redeemedAt?.toDate?.() || 0;
                    const bTime = b.redeemedAt?.toDate?.() || 0;
                    return bTime - aTime;
                });
            setRedemptions((prev) => ({ ...prev, [code]: rows }));
        } catch {
            setRedemptions((prev) => ({ ...prev, [code]: [] }));
        } finally {
            setLoadingRedemptions((prev) => ({ ...prev, [code]: false }));
        }
    };

    return (
        <PromoStatsContainer className="widget-bg">
            <HeaderContainer>
                <Title text={"PROMO CODE STATS"} />
                <PrimaryBtn title={isLoading ? 'LOADING...' : 'REFRESH'} onClick={fetchPromoCodes} disabled={isLoading} />
            </HeaderContainer>

            {error && (
                <h3 style={{ color: '#721c24', fontSize: 13 }}>Error loading promo code stats.</h3>
            )}

            {isLoading ? (
                <LoadingState>Loading promo code data...</LoadingState>
            ) : promoCodes.length === 0 ? (
                <EmptyState>No promo codes have been redeemed yet.</EmptyState>
            ) : (
                <StatsTable>
                    <thead>
                        <tr>
                            <th>Code</th>
                            <th>Total Redemptions</th>
                            <th>Last Redeemed</th>
                            <th>Last Redeemed By</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {promoCodes.map((pc) => (
                            <>
                                <tr key={pc.id}>
                                    <td><CodeBadge>{pc.code || pc.id}</CodeBadge></td>
                                    <td><CountBadge>{pc.totalRedemptions || 0}</CountBadge></td>
                                    <td>{formatDate(pc.lastRedeemedAt)}</td>
                                    <td>
                                        {pc.lastRedeemedBy ? (
                                            <span>
                                                {pc.lastRedeemedBy.username || '—'}
                                                {pc.lastRedeemedBy.email && (
                                                    <span style={{ color: '#FFFFFF', marginLeft: 6, fontSize: 12 }}>
                                                        ({pc.lastRedeemedBy.email})
                                                    </span>
                                                )}
                                            </span>
                                        ) : '—'}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <PrimaryBtn title={expandedCode === pc.id ? 'HIDE' : 'VIEW ALL'} onClick={() => handleToggleExpand(pc.id)} />
                                    </td>
                                </tr>

                                {expandedCode === pc.id && (
                                    <tr key={`${pc.id}-expanded`}>
                                        <td colSpan={5} style={{ paddingTop: 0 }}>
                                            {loadingRedemptions[pc.id] ? (
                                                <LoadingState style={{ padding: '12px' }}>Loading...</LoadingState>
                                            ) : (redemptions[pc.id] || []).length === 0 ? (
                                                <EmptyState style={{ padding: '12px' }}>No redemption records found.</EmptyState>
                                            ) : (
                                                <RedemptionList>
                                                    <RedemptionHeader>
                                                        <span>Username</span>
                                                        <span>Email</span>
                                                        <span>User ID</span>
                                                        <span>Redeemed At</span>
                                                    </RedemptionHeader>
                                                    {redemptions[pc.id].map((r) => (
                                                        <RedemptionRow key={r.id}>
                                                            <span>{r.username || '—'}</span>
                                                            <span>{r.email || '—'}</span>
                                                            <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#FFFFFF' }}>
                                                                {r.userId}
                                                            </span>
                                                            <span>{formatDate(r.redeemedAt)}</span>
                                                        </RedemptionRow>
                                                    ))}
                                                </RedemptionList>
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </>
                        ))}
                    </tbody>
                </StatsTable>
            )}
        </PromoStatsContainer>
    );
}
